import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../config';

function AcceptInviteComplete() {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const dumpAuth0State = useCallback(() => {
    console.log("== AUTH0 STATE DUMP ==");
    console.log("location.state:", location.state);
    console.log("location.search:", location.search);
    
    // Try to extract appState
    if (location.state && location.state.appState) {
      console.log("appState found:", location.state.appState);
      
      // If there's a state parameter, try to decode it
      if (location.state.appState.state) {
        try {
          const decoded = JSON.parse(atob(location.state.appState.state));
          console.log("Decoded state:", decoded);
        } catch (e) {
          console.log("Failed to decode state:", e);
        }
      }
    }
    console.log("== END STATE DUMP ==");
  }, [location]);

  useEffect(() => {
    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.error("Invitation acceptance timed out");
        setError("The invitation process is taking too long. Please try again or check your internet connection.");
        setLoading(false);
      }
    }, 30000); // 30 second timeout

    async function acceptInvitation() {
      // Clear timeout if we start processing
      clearTimeout(timeoutId);
      
      // Debug logging for incognito mode issues
      console.log("AcceptInviteComplete state:", {
        isLoading,
        isAuthenticated,
        hasUser: !!user,
        userEmail: user?.email,
        isIncognito: navigator.userAgent.includes('Chrome') && !window.navigator.storage
      });
      
      // Handle authentication failures
      if (!isLoading && !isAuthenticated) {
        console.error("User not authenticated in AcceptInviteComplete");
        setError("Authentication failed. Please try signing in again.");
        setLoading(false);
        return;
      }
      
      // Handle missing user data
      if (!isLoading && isAuthenticated && !user) {
        console.error("User authenticated but no user data available");
        setError("User data not available. Please try signing in again.");
        setLoading(false);
        return;
      }

      // If still loading after a reasonable time, show a more helpful message
      if (isLoading) {
        console.log("Still loading authentication...");
        return; // Let Auth0 continue loading
      }

      if (!isLoading && isAuthenticated && user) {
        dumpAuth0State();
        try {
          console.log("Auth completed, looking for invitation token...");
          
          // SECURE TOKEN RETRIEVAL STRATEGY (sessionStorage only)
          let token = null;
          
          // 1. Check URL parameters (most direct)
          const urlParams = new URLSearchParams(window.location.search);
          token = urlParams.get('invitation_token') || urlParams.get('token');
          console.log("Token from URL params:", token);
          
          // 2. Check sessionStorage (secure temporary storage)
          if (!token) {
            token = sessionStorage.getItem('invitation_token');
            console.log("Token from sessionStorage:", token);
          }
          
          // 3. Check sessionStorage with timestamp data
          if (!token) {
            try {
              const tokenData = JSON.parse(sessionStorage.getItem('invitation_data') || '{}');
              if (tokenData.token && Date.now() - tokenData.timestamp < 600000) { // 10 minutes
                token = tokenData.token;
                console.log("Token from sessionStorage with timestamp:", token);
              }
            } catch (e) {
              console.error("Error parsing session token data:", e);
            }
          }
          
          // 4. Check Auth0 appState (fallback method)
          if (!token) {
            const state = location.state?.appState?.state;
            if (state) {
              try {
                const decodedState = JSON.parse(atob(state));
                token = decodedState.invitationToken;
                console.log("Token from Auth0 state:", token);
              } catch (e) {
                console.error("Error parsing Auth0 state:", e);
              }
            }
          }
          
          // 5. Check Auth0 direct appState
          if (!token && location.state?.appState?.invitation_token) {
            token = location.state.appState.invitation_token;
            console.log("Token from Auth0 appState direct:", token);
          }
          
          // 6. Try to decode Auth0 state parameter from URL
          if (!token) {
            try {
              const hash = window.location.hash;
              if (hash.includes('state=')) {
                const stateMatch = hash.match(/state=([^&]+)/);
                if (stateMatch) {
                  const decodedState = JSON.parse(atob(decodeURIComponent(stateMatch[1])));
                  token = decodedState.invitation_token;
                  console.log("Token from Auth0 hash state:", token);
                }
              }
            } catch (e) {
              console.error("Error parsing Auth0 hash state:", e);
            }
          }
          
          // If still no token, show error
          if (!token) {
            console.error("No invitation token found in any location");
            console.log("Available data sources checked:");
            console.log("- URL params:", window.location.search);
            console.log("- sessionStorage invitation_token:", sessionStorage.getItem('invitation_token'));
            console.log("- sessionStorage invitation_data:", sessionStorage.getItem('invitation_data'));
            console.log("- Auth0 state:", location.state);
            setError("Invitation token not found. Please try the invitation link again.");
            setLoading(false);
            return;
          }
          
          console.log("Using token to accept invitation:", token);
          
          // Check if this invitation has already been processed to prevent duplicates
          const processedKey = `invitation_processed_${token}`;
          if (sessionStorage.getItem(processedKey)) {
            console.log("Invitation has already been processed, redirecting to dashboard");
            sessionStorage.removeItem('invitation_token');
            sessionStorage.removeItem('invitation_data');
            navigate('/dashboard', { replace: true });
            return;
          }
          
          // Clear stored tokens to prevent reuse
          sessionStorage.removeItem('invitation_token');
          
          // Process the invitation
          const accessToken = await getAccessTokenSilently();
          
          console.log("Making API request to accept invitation with token:", token);
          console.log("User data:", { 
            auth0UserId: user.sub, 
            email: user.email,
            name: user.name || user.nickname 
          });
          
          const response = await fetch(`${API_BASE_URL}/api/invitations/${token}/accept`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
              auth0UserId: user.sub,
              email: user.email,
              name: user.name || user.nickname || user.email?.split('@')[0]
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            console.error("API Error:", errorData);
            console.log("Full error response:", JSON.stringify(errorData, null, 2));
            
            // Handle specific error cases more gracefully
            if (errorData.error === 'Invitation already used') {
              console.log("Invitation already used, checking if already processed");
              // Mark as processed and redirect to organization dashboard
              sessionStorage.setItem(`invitation_processed_${token}`, 'true');
              sessionStorage.removeItem('invitation_data');
              
              // Try to get organization info for proper redirect
              try {
                const orgResponse = await fetch(`${API_BASE_URL}/api/organizations`, {
                  headers: { 'Authorization': `Bearer ${accessToken}` }
                });
                if (orgResponse.ok) {
                  const orgs = await orgResponse.json();
                  if (orgs.length > 0) {
                    const orgUniqueId = orgs[0].unique_id;
                    navigate(`/org/${orgUniqueId}/dashboard`, { replace: true });
                    return;
                  }
                }
              } catch (orgError) {
                console.warn("Could not fetch organization for redirect:", orgError);
              }
              
              // Fallback to general dashboard
              navigate('/dashboard', { replace: true });
              return;
            }
            
            // Extract the error message properly
            const errorMessage = errorData.error || errorData.message || 'Failed to accept invitation';
            console.log("Extracted error message:", errorMessage);
            
            throw new Error(errorMessage);
          }
          
          const result = await response.json();
          console.log("Invitation accepted successfully:", result);
          
          // Mark invitation as processed to prevent duplicate processing
          sessionStorage.setItem(`invitation_processed_${token}`, 'true');
          
          // Clean up invitation data
          sessionStorage.removeItem('invitation_data');
          
          // CRITICAL: Force token refresh to get updated organization information
          console.log("Forcing token refresh to get updated organization claims...");
          const freshToken = await getAccessTokenSilently({ 
            cacheMode: 'off' // Force fresh token from Auth0
          });
          console.log("Fresh token obtained successfully");
          
          // Navigate to organization-specific dashboard
          try {
            // First, try to get organization info from the invitation result
            let orgUniqueId = result.organization?.unique_id;
            
            // If not in result, fetch user's organizations with fresh token
            if (!orgUniqueId) {
              const orgResponse = await fetch(`${API_BASE_URL}/api/organizations`, {
                headers: { 'Authorization': `Bearer ${freshToken}` }
              });
              if (orgResponse.ok) {
                const orgs = await orgResponse.json();
                if (orgs.length > 0) {
                  orgUniqueId = orgs[0].unique_id;
                }
              }
            }
            
            // Navigate to organization dashboard if we have the ID
            if (orgUniqueId) {
              console.log("Navigating to organization dashboard:", `/org/${orgUniqueId}/dashboard`);
              navigate(`/org/${orgUniqueId}/dashboard`, { replace: true });
            } else {
              console.warn("No organization ID found, falling back to general dashboard");
              navigate('/dashboard', { replace: true });
            }
          } catch (navError) {
            console.warn("Error getting organization for navigation:", navError);
            navigate('/dashboard', { replace: true });
          }
        } catch (err) {
          console.error("Error accepting invitation:", err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    }
    
    acceptInvitation();
    
    // Cleanup timeout on component unmount
    return () => clearTimeout(timeoutId);
  }, [isLoading, isAuthenticated, user, navigate, getAccessTokenSilently, location, dumpAuth0State, loading]);
  
  if (error) {
    // Check if this is an email mismatch error
    const isEmailMismatch = error.includes('EMAIL_MISMATCH:');
    const errorMessage = isEmailMismatch ? error.replace('EMAIL_MISMATCH:', '').trim() : error;
    
    console.log("Error display check:", { 
      rawError: error, 
      isEmailMismatch, 
      processedMessage: errorMessage 
    });
    
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="p-8 bg-white shadow-lg rounded-lg max-w-md w-full">
          <div className="text-center">
            <div className={`text-5xl mb-4 ${isEmailMismatch ? 'text-orange-500' : 'text-red-500'}`}>
              {isEmailMismatch ? '🔒' : '⚠️'}
            </div>
            <h1 className={`text-2xl font-bold mb-2 ${isEmailMismatch ? 'text-orange-800' : 'text-gray-800'}`}>
              {isEmailMismatch ? 'Email Address Mismatch' : 'Invitation Error'}
            </h1>
            
            {isEmailMismatch ? (
              <div className="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-orange-800 font-medium mb-2">Security Notice</p>
                <p className="text-orange-700 text-sm mb-3">{errorMessage}</p>
                <p className="text-orange-600 text-xs">
                  This security measure prevents unauthorized access to invitations.
                </p>
              </div>
            ) : (
              <p className="text-gray-600 mb-4">{errorMessage}</p>
            )}
            
            {/* Additional help for incognito mode */}
            {error.includes("Authentication failed") && (
              <div className="text-sm text-gray-500 mb-4 p-3 bg-yellow-50 rounded-lg">
                <p className="font-medium text-yellow-800 mb-1">💡 Tip for Incognito Mode:</p>
                <p>If you're using incognito/private browsing, try:</p>
                <ul className="list-disc list-inside mt-2 text-left">
                  <li>Opening the invitation link in a regular browser window</li>
                  <li>Allowing cookies and storage for this site</li>
                  <li>Disabling strict tracking protection temporarily</li>
                </ul>
              </div>
            )}
            
            <div className="space-x-3">
              {isEmailMismatch ? (
                <>
                  <button 
                    onClick={() => logout({ 
                      logoutParams: { 
                        returnTo: window.location.origin + '/' 
                      } 
                    })}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  >
                    Sign Out & Try Again
                  </button>
                  <button 
                    onClick={() => navigate('/')}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    Go to Home
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => navigate('/')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Go to Home
                  </button>
                  <button 
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    Try Again
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="p-8 bg-white shadow-lg rounded-lg max-w-md w-full">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
        <p className="text-center mt-4 text-gray-600">Completing your account setup...</p>
      </div>
    </div>
  );
}

export default AcceptInviteComplete;